import { z } from 'zod'
import type { ApiClient } from './client'
import { cmMs, cmStatus, cmStatuses, createCmCall, type CmCategory, type CmScope, type CmStatus } from './case-mgmt'

/**
 * Case management V2 — the tenant settings the console edits (Settings ›
 * Products › Cases). Same service and the same scope-attaching call as
 * case-mgmt.ts. Contract read from the service's routers and schemas
 * (case_mgmt_service, branch dev-case-mgmt-v2-colocate, 2026-10-13).
 *
 *   GET  /statuses?include_archived       the catalogue, archived included
 *   POST /statuses                        create a custom status (c_<slug>)
 *   PUT  /statuses/{key}                  label · category · sort_order · flags (flags replace whole)
 *   POST /statuses/{key}/archive|unarchive
 *   GET  /statuses/edges                  the tenant's allow / block edges
 *   POST /statuses/edges                  set one pair to allow or block (upsert)
 *   GET  /settings/workflow-presets       closure presets; POST /{name}/apply|revert?dry_run
 *   GET  /settings/features               the five flags (admin only)
 *   PUT  /settings/features/{flag}        { enabled }
 *   GET  /itsm/schema                     the webhook form: providers + fields
 *   GET  /itsm/settings, PUT              the tenant's V2 ITSM switch
 *   GET  /itsm/webhooks, /{id}            connections; secrets come back blank
 *   POST /itsm/webhooks                   create (the callback token, shown once)
 *   PUT  /itsm/webhooks/{id}              edit (a configured one saves to pending)
 *   POST /itsm/webhooks/{id}/verify, /verify/cancel, /token/rotate
 *        (verify answers the run's verification_code — the read never has it)
 *   GET  /itsm/webhooks/{id}/presets      payload presets, by name (Epic 19)
 *   POST /itsm/webhooks/{id}/presets      create; PUT /{preset} replace; DELETE /{preset}
 *   GET  /itsm/outbox, POST /itsm/outbox/{id}/retry
 *   GET  /settings/grouping, PUT          auto-grouping and relations (partial)
 *   GET  /settings/evidence, PUT          retention · quota · types (partial)
 *
 * The only DELETE here is a payload preset's: statuses are archived, a
 * transition edge is flipped between allow and block (never removed), and an
 * ITSM connection is never deleted from here.
 */

/* --------------------------------------------------------------- statuses */

const optStr = z.string().nullable().optional().transform((v) => v ?? '')

/** The status flags the service knows; the last two need `workflow_templates`. */
export const CM_STATUS_FLAGS = ['require_reason', 'require_assignee', 'require_note', 'require_resolution_reason', 'require_root_cause'] as const
export type CmStatusFlag = (typeof CM_STATUS_FLAGS)[number]

/** Built-in transitions — fixed by the service; a block on one is ignored. */
export const CM_BUILTIN_EDGES: readonly (readonly [string, string])[] = [
  ['NEW', 'IN-PROGRESS'], ['NEW', 'ON-HOLD'], ['NEW', 'CLOSED'],
  ['IN-PROGRESS', 'ON-HOLD'], ['IN-PROGRESS', 'CLOSED'],
  ['ON-HOLD', 'IN-PROGRESS'], ['ON-HOLD', 'CLOSED'],
  ['CLOSED', 'NEW'],
]

/** Custom status keys: `c_<slug>`, 30 characters in all. */
export const CM_STATUS_KEY_MAX = 30
export const CM_STATUS_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9]|_(?!_))*$/

export const cmStatusEdge = z
  .object({ from_key: z.string(), to_key: z.string(), kind: z.enum(['allow', 'block']) })
  .transform((e) => ({ from: e.from_key, to: e.to_key, kind: e.kind }))
export type CmStatusEdge = z.output<typeof cmStatusEdge>

export interface CmStatusInput {
  label: string
  category: CmCategory
  slug?: string
  order?: number
  flags?: Partial<Record<CmStatusFlag, boolean>>
}

export interface CmStatusPatch {
  label?: string
  category?: CmCategory
  order?: number
  /** Replaces every flag. */
  flags?: Partial<Record<CmStatusFlag, boolean>>
}

/** The flags of a catalogue row, as the service spells them. */
export const statusFlags = (s: CmStatus): Record<CmStatusFlag, boolean> => ({
  require_reason: s.requireReason,
  require_assignee: s.requireAssignee,
  require_note: s.requireNote,
  require_resolution_reason: s.requireResolutionReason,
  require_root_cause: s.requireRootCause,
})

/** Closure presets: set flags on every active custom status of a category (needs `workflow_templates`). */
export const cmPresets = z
  .object({
    workflow_templates_enabled: z.boolean(),
    presets: z.array(
      z.object({
        name: z.string(),
        title: z.string(),
        description: optStr,
        applicable: z.boolean(),
        state: z.string(),
        targets: z.array(z.string()).default([]),
      }),
    ),
  })
  .transform((r) => ({
    enabled: r.workflow_templates_enabled,
    presets: r.presets.map((p) => ({
      name: p.name,
      title: p.title,
      description: p.description,
      applicable: p.applicable,
      state: p.state as 'not_applicable' | 'not_applied' | 'partial' | 'applied',
      targets: p.targets,
    })),
  }))
export type CmPresets = z.output<typeof cmPresets>

export const cmPresetResult = z
  .object({
    name: z.string(),
    action: z.enum(['apply', 'revert']),
    dry_run: z.boolean(),
    applicable: z.boolean(),
    changed: z.boolean(),
    changes: z.array(z.object({ key: z.string(), before: z.record(z.string(), z.boolean()), after: z.record(z.string(), z.boolean()) })),
    skipped: z.array(z.object({ key: z.string(), reason: z.string() })),
  })
  .transform((r) => ({ ...r, dryRun: r.dry_run }))
export type CmPresetResult = z.output<typeof cmPresetResult>

/* ------------------------------------------------------------------ flags */

export const CM_FEATURE_FLAGS = ['custom_statuses', 'itsm_auto_sync', 'workflow_templates', 'evidence_attachments', 'sla_tracking'] as const
export type CmFeatureFlag = (typeof CM_FEATURE_FLAGS)[number]

const cmFlagState = z
  .object({ enabled: z.boolean(), default: z.boolean(), overridden: z.boolean().default(false), operator_managed: z.boolean().default(false) })
  .transform((f) => ({ enabled: f.enabled, default: f.default, overridden: f.overridden, operatorManaged: f.operator_managed }))
export type CmFlagState = z.output<typeof cmFlagState>
export const cmFeatureFlags = z.object({ flags: z.record(z.string(), cmFlagState) }).transform((r) => r.flags)

/* ------------------------------------------------------------------- ITSM */

export const CM_ITSM_STATES = ['NOT_CONFIGURED', 'WAITING_CALLBACK', 'CONFIGURED', 'FAILED'] as const
export type CmItsmState = (typeof CM_ITSM_STATES)[number]

export const cmItsmSchema = z
  .object({
    providers: z.array(
      z.object({ value: z.string(), label: z.string(), description: optStr, defaults: z.record(z.string(), z.string()).nullable().optional() }),
    ),
    fields: z.array(
      z.object({
        name: z.string(),
        label: optStr,
        type: z.string(),
        required: z.boolean().default(false),
        default: optStr,
        placeholder: optStr,
        help_text: optStr,
        options: z.array(z.object({ value: z.string(), label: optStr })).nullable().optional(),
        visible_when: z
          .object({ field: z.string(), value: z.string().nullable().optional(), values: z.array(z.string()).nullable().optional() })
          .nullable()
          .optional(),
        sensitive: z.boolean().default(false),
      }),
    ),
  })
  .transform((r) => ({
    providers: r.providers.map((p) => ({ value: p.value, label: p.label, description: p.description, defaults: p.defaults ?? {} })),
    fields: r.fields.map((f) => ({
      name: f.name,
      label: f.label || f.name,
      type: f.type as 'input' | 'select' | 'long_text' | 'key_value_mapper' | 'readonly',
      required: f.required,
      default: f.default,
      placeholder: f.placeholder,
      help: f.help_text,
      options: (f.options ?? []).map((o) => ({ value: o.value, label: o.label || o.value })),
      visibleWhen: f.visible_when
        ? { field: f.visible_when.field, values: f.visible_when.values ?? (typeof f.visible_when.value === 'string' ? [f.visible_when.value] : []) }
        : null,
      sensitive: f.sensitive,
    })),
  }))
export type CmItsmSchema = z.output<typeof cmItsmSchema>
export type CmItsmField = CmItsmSchema['fields'][number]

export const cmItsmSettings = z
  .object({ v2_itsm_enabled: z.boolean(), updated_at: cmMs, updated_by: z.string().nullable().optional() })
  .transform((s) => ({ enabled: s.v2_itsm_enabled, updatedAt: s.updated_at, updatedBy: s.updated_by ?? null }))
export type CmItsmSettings = z.output<typeof cmItsmSettings>

/** A webhook's config values: strings, and an object for the status mapper. */
export type CmItsmConfig = Record<string, string | Record<string, string>>

const config = z.record(z.string(), z.unknown()).nullable().optional().transform((c) => (c ?? {}) as CmItsmConfig)

export const cmItsmWebhook = z
  .object({
    id: z.string(),
    name: z.string(),
    provider: z.string(),
    config,
    config_status: z.string().transform((v) => v as CmItsmState),
    has_pending_config: z.boolean().default(false),
    verification_locked: z.boolean().default(false),
    verification: z
      .object({
        state: z.enum(['waiting', 'failed', 'verified']),
        test_ticket_id: z.string().nullable().optional(),
        started_at: z.number().nullable().optional(),
        verified_at: z.number().nullable().optional(),
        last_error: z.string().nullable().optional(),
        note_received: z.boolean().default(false),
        note_received_at: z.number().nullable().optional(),
        steps: z.object({ ticket_created: z.boolean(), note_received: z.boolean(), closed: z.boolean() }).partial().optional(),
      })
      .nullable()
      .optional(),
    pending_config: config,
    created_at: cmMs,
    updated_at: cmMs,
    callback_token: z.string().optional(),
    saved_to: z.enum(['config', 'pending']).optional(),
  })
  .transform((w) => ({
    id: w.id,
    name: w.name,
    provider: w.provider,
    config: w.config,
    pendingConfig: Object.keys(w.pending_config).length ? w.pending_config : null,
    status: w.config_status,
    hasPending: w.has_pending_config,
    locked: w.verification_locked,
    verification: w.verification
      ? {
          state: w.verification.state,
          testTicketId: w.verification.test_ticket_id ?? null,
          startedAt: w.verification.started_at ?? null,
          verifiedAt: w.verification.verified_at ?? null,
          lastError: w.verification.last_error ?? null,
          /** The customer's note with the run's code arrived (Epic 20). */
          noteReceived: w.verification.note_received,
          /** Not kept once verified. */
          noteReceivedAt: w.verification.note_received_at ?? null,
          /** Ticket created → note received → closed; derived as the service does when it sends none. */
          steps: {
            ticketCreated: w.verification.steps?.ticket_created ?? Boolean(w.verification.test_ticket_id),
            noteReceived: w.verification.steps?.note_received ?? (w.verification.note_received || w.verification.state === 'verified'),
            closed: w.verification.steps?.closed ?? w.verification.state === 'verified',
          },
        }
      : null,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    /** Create only — the inbound token, shown once. */
    callbackToken: w.callback_token ?? null,
    /** Update only — a configured connection saves to pending until verified. */
    savedTo: w.saved_to ?? null,
  }))
export type CmItsmWebhook = z.output<typeof cmItsmWebhook>

/** Payload presets: at most 20 per connection, names 1–100 characters and unique per connection. */
export const CM_ITSM_PRESETS_MAX = 20
export const CM_ITSM_PRESET_NAME_MAX = 100

/**
 * A named `extra_payload` saved on a connection (Epic 19): picked by id at a
 * manual sync. Plain JSON every analyst of the scope can read — never secrets.
 */
export const cmItsmPreset = z
  .object({
    id: z.string(),
    webhook_id: z.string(),
    name: z.string(),
    payload: z.record(z.string(), z.unknown()),
    created_by: optStr,
    created_at: cmMs,
    updated_at: cmMs,
  })
  .transform((p) => ({
    id: p.id,
    webhookId: p.webhook_id,
    name: p.name,
    payload: p.payload,
    createdBy: p.created_by,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  }))
export type CmItsmPreset = z.output<typeof cmItsmPreset>
export interface CmItsmPresetInput {
  name: string
  payload: Record<string, unknown>
}

export const cmItsmVerify = z
  .object({
    result: z.enum(['waiting', 'cancelled']),
    config_status: z.string().transform((v) => v as CmItsmState),
    test_ticket_id: z.string().nullable().optional(),
    message: optStr,
    verification_code: z.string().nullable().optional(),
  })
  .transform((r) => ({
    result: r.result,
    status: r.config_status,
    testTicketId: r.test_ticket_id ?? null,
    message: r.message,
    /** The run's code (`VERIFY-XXXX`) for the customer's note: only in this answer, never in the read. */
    code: r.verification_code ?? null,
  }))
export type CmItsmVerify = z.output<typeof cmItsmVerify>

export const CM_OUTBOX_STATES = ['pending', 'in_flight', 'done', 'failed'] as const
export type CmOutboxState = (typeof CM_OUTBOX_STATES)[number]

export const cmItsmOutbox = z
  .object({
    items: z.array(
      z.object({
        id: z.string(),
        case_id: z.string(),
        config_id: z.string(),
        event_type: z.string(),
        status: z.enum(CM_OUTBOX_STATES),
        attempts: z.number().default(0),
        next_attempt_at: cmMs,
        last_error: z.string().nullable().optional(),
        created_at: cmMs,
        updated_at: cmMs,
      }),
    ),
    counts: z.object({ pending: z.number(), in_flight: z.number(), done: z.number(), failed: z.number() }),
    limit: z.number(),
    offset: z.number(),
  })
  .transform((r) => ({
    items: r.items.map((x) => ({
      id: x.id,
      caseId: x.case_id,
      configId: x.config_id,
      event: x.event_type,
      status: x.status,
      attempts: x.attempts,
      nextAttemptAt: x.next_attempt_at,
      lastError: x.last_error ?? null,
      createdAt: x.created_at,
      updatedAt: x.updated_at,
    })),
    counts: r.counts,
    limit: r.limit,
    offset: r.offset,
  }))
export type CmItsmOutbox = z.output<typeof cmItsmOutbox>

/* --------------------------------------------------------------- grouping */

export const CM_GROUPING_INTERVALS = [5, 15, 30, 60, 240, 1440] as const
export const CM_SIMILAR_WINDOWS = [60, 240, 720, 1440, 4320, 10080] as const
export const CM_RELATION_WINDOWS = [7, 14, 30] as const
export const CM_RELATION_ENTITY_TYPES = ['host', 'user', 'ip', 'hash', 'url'] as const
export const CM_IGNORE_VALUES_MAX = 500

export const cmGrouping = z
  .object({
    auto_grouping_interval_minutes: z.number().nullable(),
    add_similar: z.boolean(),
    similar_window_minutes: z.number(),
    case_relations_enabled: z.boolean(),
    relation_window_days: z.number(),
    relation_min_shared: z.number(),
    relation_entity_types: z.array(z.string()),
    ignore_values: z.array(z.string()),
    similar_max_per_group: z.number().nullable(),
  })
  .transform((g) => ({
    intervalMinutes: g.auto_grouping_interval_minutes,
    addSimilar: g.add_similar,
    similarWindowMinutes: g.similar_window_minutes,
    relationsEnabled: g.case_relations_enabled,
    relationWindowDays: g.relation_window_days,
    relationMinShared: g.relation_min_shared,
    relationEntityTypes: g.relation_entity_types,
    ignoreValues: g.ignore_values,
    similarMaxPerGroup: g.similar_max_per_group,
  }))
export type CmGrouping = z.output<typeof cmGrouping>

const GROUPING_WIRE: Record<keyof CmGrouping, string> = {
  intervalMinutes: 'auto_grouping_interval_minutes',
  addSimilar: 'add_similar',
  similarWindowMinutes: 'similar_window_minutes',
  relationsEnabled: 'case_relations_enabled',
  relationWindowDays: 'relation_window_days',
  relationMinShared: 'relation_min_shared',
  relationEntityTypes: 'relation_entity_types',
  ignoreValues: 'ignore_values',
  similarMaxPerGroup: 'similar_max_per_group',
}

/* --------------------------------------------------------------- evidence */

export const cmEvidenceSettings = z
  .object({
    retention_days: z.number(),
    quota_bytes: z.number().nullable(),
    allowed_types: z.array(z.string()).nullable(),
    effective_quota_bytes: z.number(),
    effective_allowed_types: z.array(z.string()),
    platform_quota_bytes: z.number(),
    quota_ceiling_bytes: z.number(),
    quota_source: z.enum(['platform', 'operator', 'tenant']),
    platform_allowed_types: z.array(z.string()),
    min_quota_bytes: z.number().default(1_048_576),
    min_retention_days: z.number().default(1),
    max_retention_days: z.number().default(365),
  })
  .transform((e) => ({
    retentionDays: e.retention_days,
    quotaBytes: e.quota_bytes,
    allowedTypes: e.allowed_types,
    effectiveQuotaBytes: e.effective_quota_bytes,
    effectiveAllowedTypes: e.effective_allowed_types,
    platformQuotaBytes: e.platform_quota_bytes,
    quotaCeilingBytes: e.quota_ceiling_bytes,
    quotaSource: e.quota_source,
    platformAllowedTypes: e.platform_allowed_types,
    minQuotaBytes: e.min_quota_bytes,
    minRetentionDays: e.min_retention_days,
    maxRetentionDays: e.max_retention_days,
  }))
export type CmEvidenceSettings = z.output<typeof cmEvidenceSettings>

/** A partial evidence update; null clears the tenant override. */
export interface CmEvidencePatch {
  retentionDays?: number
  quotaBytes?: number | null
  allowedTypes?: string[] | null
}

/* -------------------------------------------------------------------- api */

export interface CaseSettingsApi {
  statuses(scope: CmScope, opts?: { includeArchived?: boolean }): Promise<CmStatus[]>
  createStatus(scope: CmScope, input: CmStatusInput): Promise<CmStatus>
  updateStatus(scope: CmScope, key: string, patch: CmStatusPatch): Promise<CmStatus>
  archiveStatus(scope: CmScope, key: string): Promise<CmStatus>
  unarchiveStatus(scope: CmScope, key: string): Promise<CmStatus>
  edges(scope: CmScope): Promise<CmStatusEdge[]>
  /** Upsert: an existing edge for the pair has its kind replaced. */
  setEdge(scope: CmScope, edge: CmStatusEdge): Promise<CmStatusEdge>
  presets(scope: CmScope): Promise<CmPresets>
  /** `dryRun` (the service's default) reports the changes and writes nothing. */
  runPreset(scope: CmScope, name: string, action: 'apply' | 'revert', dryRun: boolean): Promise<CmPresetResult>
  features(scope: CmScope): Promise<Record<string, CmFlagState>>
  setFeature(scope: CmScope, flag: CmFeatureFlag, enabled: boolean): Promise<CmFlagState>
  itsmSchema(scope: CmScope): Promise<CmItsmSchema>
  itsmSettings(scope: CmScope): Promise<CmItsmSettings>
  setItsmEnabled(scope: CmScope, enabled: boolean): Promise<CmItsmSettings>
  webhooks(scope: CmScope): Promise<CmItsmWebhook[]>
  webhook(scope: CmScope, id: string): Promise<CmItsmWebhook>
  createWebhook(scope: CmScope, input: { name: string; provider: string; config: CmItsmConfig }): Promise<CmItsmWebhook>
  updateWebhook(scope: CmScope, id: string, input: { name: string; provider: string; config: CmItsmConfig }): Promise<CmItsmWebhook>
  verifyWebhook(scope: CmScope, id: string): Promise<CmItsmVerify>
  cancelVerify(scope: CmScope, id: string): Promise<CmItsmVerify>
  rotateToken(scope: CmScope, id: string): Promise<{ callbackToken: string; note: string }>
  /** A connection's payload presets, by name (analysts read them too). */
  itsmPresets(scope: CmScope, webhookId: string): Promise<CmItsmPreset[]>
  createItsmPreset(scope: CmScope, webhookId: string, input: CmItsmPresetInput): Promise<CmItsmPreset>
  /** Replaces both the name and the payload. */
  updateItsmPreset(scope: CmScope, webhookId: string, presetId: string, input: CmItsmPresetInput): Promise<CmItsmPreset>
  deleteItsmPreset(scope: CmScope, webhookId: string, presetId: string): Promise<void>
  outbox(scope: CmScope, q: { status?: CmOutboxState; limit: number; offset: number }): Promise<CmItsmOutbox>
  retryOutbox(scope: CmScope, rowId: string): Promise<void>
  grouping(scope: CmScope): Promise<CmGrouping>
  updateGrouping(scope: CmScope, patch: Partial<CmGrouping>): Promise<CmGrouping>
  evidence(scope: CmScope): Promise<CmEvidenceSettings>
  updateEvidence(scope: CmScope, patch: CmEvidencePatch): Promise<CmEvidenceSettings>
}

export function createCaseSettingsApi(client: ApiClient): CaseSettingsApi {
  const enc = encodeURIComponent
  const call = createCmCall(client)
  const webhookBody = (i: { name: string; provider: string; config: CmItsmConfig }) => ({ name: i.name.trim(), provider: i.provider, config: i.config })
  const presetBody = (i: CmItsmPresetInput) => ({ name: i.name.trim(), payload: i.payload })

  return {
    statuses: (s, o = {}) => call(s, 'GET', `/statuses${o.includeArchived ? '?include_archived=true' : ''}`, { schema: cmStatuses }),
    createStatus: (s, i) =>
      call(s, 'POST', '/statuses', {
        body: {
          label: i.label.trim(),
          category: i.category,
          ...(i.slug?.trim() ? { slug: i.slug.trim() } : {}),
          sort_order: i.order ?? 0,
          flags: i.flags ?? {},
        },
        schema: cmStatus,
      }),
    updateStatus: (s, key, p) =>
      call(s, 'PUT', `/statuses/${enc(key)}`, {
        body: {
          ...(p.label !== undefined ? { label: p.label.trim() } : {}),
          ...(p.category !== undefined ? { category: p.category } : {}),
          ...(p.order !== undefined ? { sort_order: p.order } : {}),
          ...(p.flags !== undefined ? { flags: p.flags } : {}),
        },
        schema: cmStatus,
      }),
    archiveStatus: (s, key) => call(s, 'POST', `/statuses/${enc(key)}/archive`, { schema: cmStatus }),
    unarchiveStatus: (s, key) => call(s, 'POST', `/statuses/${enc(key)}/unarchive`, { schema: cmStatus }),
    edges: (s) => call(s, 'GET', '/statuses/edges', { schema: z.array(cmStatusEdge) }),
    setEdge: (s, e) => call(s, 'POST', '/statuses/edges', { body: { from_key: e.from, to_key: e.to, kind: e.kind }, schema: cmStatusEdge }),
    presets: (s) => call(s, 'GET', '/settings/workflow-presets', { schema: cmPresets }),
    runPreset: (s, name, action, dryRun) =>
      call(s, 'POST', `/settings/workflow-presets/${enc(name)}/${action}?dry_run=${dryRun}`, { schema: cmPresetResult }),
    features: (s) => call(s, 'GET', '/settings/features', { schema: cmFeatureFlags }),
    setFeature: (s, flag, enabled) => call(s, 'PUT', `/settings/features/${enc(flag)}`, { body: { enabled }, schema: cmFlagState }),
    itsmSchema: (s) => call(s, 'GET', '/itsm/schema', { schema: cmItsmSchema }),
    itsmSettings: (s) => call(s, 'GET', '/itsm/settings', { schema: cmItsmSettings }),
    setItsmEnabled: (s, enabled) => call(s, 'PUT', '/itsm/settings', { body: { v2_itsm_enabled: enabled }, schema: cmItsmSettings }),
    webhooks: (s) => call(s, 'GET', '/itsm/webhooks', { schema: z.array(cmItsmWebhook) }),
    webhook: (s, id) => call(s, 'GET', `/itsm/webhooks/${enc(id)}`, { schema: cmItsmWebhook }),
    createWebhook: (s, i) => call(s, 'POST', '/itsm/webhooks', { body: webhookBody(i), schema: cmItsmWebhook }),
    updateWebhook: (s, id, i) => call(s, 'PUT', `/itsm/webhooks/${enc(id)}`, { body: webhookBody(i), schema: cmItsmWebhook }),
    // A failed step is a 400 whose body is { result: 'failed', error } — the HTTP core makes `error` the message.
    verifyWebhook: (s, id) => call(s, 'POST', `/itsm/webhooks/${enc(id)}/verify`, { body: {}, schema: cmItsmVerify }),
    cancelVerify: (s, id) => call(s, 'POST', `/itsm/webhooks/${enc(id)}/verify/cancel`, { body: {}, schema: cmItsmVerify }),
    rotateToken: (s, id) =>
      call(s, 'POST', `/itsm/webhooks/${enc(id)}/token/rotate`, {
        schema: z.object({ callback_token: z.string(), note: optStr }).transform((r) => ({ callbackToken: r.callback_token, note: r.note })),
      }),
    itsmPresets: (s, w) => call(s, 'GET', `/itsm/webhooks/${enc(w)}/presets`, { schema: z.object({ items: z.array(cmItsmPreset) }) }).then((r) => r.items),
    createItsmPreset: (s, w, i) => call(s, 'POST', `/itsm/webhooks/${enc(w)}/presets`, { body: presetBody(i), schema: cmItsmPreset }),
    updateItsmPreset: (s, w, id, i) => call(s, 'PUT', `/itsm/webhooks/${enc(w)}/presets/${enc(id)}`, { body: presetBody(i), schema: cmItsmPreset }),
    deleteItsmPreset: async (s, w, id) => {
      await call(s, 'DELETE', `/itsm/webhooks/${enc(w)}/presets/${enc(id)}`)
    },
    outbox: (s, q) => {
      const p = new URLSearchParams({ limit: String(q.limit), offset: String(q.offset) })
      if (q.status) p.set('status', q.status)
      return call(s, 'GET', `/itsm/outbox?${p}`, { schema: cmItsmOutbox })
    },
    retryOutbox: async (s, rowId) => {
      await call(s, 'POST', `/itsm/outbox/${enc(rowId)}/retry`)
    },
    grouping: (s) => call(s, 'GET', '/settings/grouping', { schema: cmGrouping }),
    updateGrouping: (s, patch) =>
      call(s, 'PUT', '/settings/grouping', {
        body: Object.fromEntries(Object.entries(patch).map(([k, v]) => [GROUPING_WIRE[k as keyof CmGrouping], v])),
        schema: cmGrouping,
      }),
    evidence: (s) => call(s, 'GET', '/settings/evidence', { schema: cmEvidenceSettings }),
    updateEvidence: (s, p) =>
      call(s, 'PUT', '/settings/evidence', {
        body: {
          ...(p.retentionDays !== undefined ? { retention_days: p.retentionDays } : {}),
          ...(p.quotaBytes !== undefined ? { quota_bytes: p.quotaBytes } : {}),
          ...(p.allowedTypes !== undefined ? { allowed_types: p.allowedTypes } : {}),
        },
        schema: cmEvidenceSettings,
      }),
  }
}
